import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  DialogTitle,
  DialogContent,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  DialogActions,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import apiClient from '../api/apiClient';
import ResponsiveAppBar from '../components/AppBar';
import RoleGuard from '../components/RoleGuard';
import DeleteWithPassword from '../components/deleteWithPassword';

// Create a theme with the same color scheme and border style as InterestCalculation
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Teal
    secondary: { main: '#925f96' }, // Purple
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: { main: '#ef4444' },
    success: { main: '#22c55e' },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '0.5px',
      background: 'linear-gradient(135deg, #77c8c7 0%, #925f96 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h6: {
      fontSize: '1.1rem',
      fontWeight: 'bold',
      color: '#1e293b',
    },
    subtitle1: {
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#64748b',
    },
    body1: {
      fontSize: '0.85rem',
      color: '#1e293b',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(119, 200, 199, 0.15)',
            background: 'linear-gradient(45deg, #77c8c7 30%, #925f96 90%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.custom-paper': {
            borderLeft: '6px solid',
            borderBottom: '6px solid',
            borderImage: 'linear-gradient(to bottom, #77c8c7, #925f96) 1', // Left border gradient
            borderBottomImage: 'linear-gradient(to right, #77c8c7, #925f96) 1', // Bottom border gradient
            borderTop: '1px solid #e0e0e0',
            borderRight: '1px solid #e0e0e0',
            borderRadius: 8,
            p: 1.5,
            mb: 3,
            background: '#ffffff',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
            transition: 'box-shadow 0.2s',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f1f5f9',
            color: '#77c8c7',
            fontWeight: '600',

          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e5e7eb',

            color: '#1e293b',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(119, 200, 199, 0.1)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            background: '#ffffff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'box-shadow 0.2s',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#77c8c7',
            },
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,

        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          background: '#ffffff',
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#77c8c7',
            },
          },
        },
      },
    },
  },
});

function AccountSummaryPage() {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editSummaryMonth, setEditSummaryMonth] = useState('');
  const [docChargePerLoan, setDocChargePerLoan] = useState(200);
  const [selectedPage, setSelectedPage] = useState('');

  const [formData, setFormData] = useState({
    summaryMonth: '',
    dailyEntries: [],
    monthlyTotals: {
      openingBalance: 0,
      totalFundReleasedToF2F: 0,
      totalNoOfLoans: 0,
      totalLoanAmount: 0,
      totalDocCharge: 0,
      totalDueAmountPaid: 0,
      totalPrincipalInDue: 0,
      totalInterestInDue: 0,
      totalOfficeExpenses: 0,
      totalOfficeRent: 0,
      totalStaffSalary: 0,
      totalFinalIncome: 0,
      totalManagersShare: 0,
      totalProfit: 0,
      totalProfitWithdrawn: 0,
      totalFundInMarket: 0,
      totalClosingBalance: 0,
    },
  });

  const columns = [
    { field: 'summaryMonth', headerName: 'Month/Year', width: 150 },
    {
      field: 'totalFundReleasedToF2F',
      headerName: 'Fund Released to VF',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalFundInMarket',
      headerName: 'Fund in Market',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    { field: 'totalNoOfLoans', headerName: 'No. of Loans', width: 120 },
    {
      field: 'totalLoanAmount',
      headerName: 'Loan Amount',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalDocCharge',
      headerName: 'Doc Charge',
      width: 120,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalDueAmountPaid',
      headerName: 'Due Amount Paid',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalPrincipalInDue',
      headerName: 'Principal in Due',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalInterestInDue',
      headerName: 'Interest in Due',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalOfficeExpenses',
      headerName: 'Office Expenses',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalOfficeRent',
      headerName: 'Office Rent',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalStaffSalary',
      headerName: 'Staff Salary',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalFinalIncome',
      headerName: 'Final Income',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalManagersShare',
      headerName: "Manager's 25%",
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'totalProfit',
      headerName: 'Profit',
      width: 150,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ mt: 2 }}
          color={(params.value ?? 0) < 0 ? 'error.main' : 'text.primary'}
        >
          Rs. {(params.value ?? 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'totalProfitWithdrawn',
      headerName: 'Profit Withdrawn',
      width: 150,
      renderCell: (params) => `Rs. ${(params.value ?? 0).toLocaleString('en-IN')}`,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
          <IconButton color="primary" size="small" onClick={() => handleEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          </RoleGuard>
          <RoleGuard allowedRoles={["Admin Manager"]}>
          <DeleteWithPassword
            resourceId={params.row.summaryMonth}
            resourceName="Summary"
            onDelete={handleDelete}
            navigate={navigate}
            renderTrigger={(onClick) => (
              <IconButton color="error" size="small" onClick={onClick}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          />
          </RoleGuard>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchSummaries();
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      monthlyTotals: {
        ...prev.monthlyTotals,
        totalDocCharge: prev.monthlyTotals.totalNoOfLoans * docChargePerLoan,
      },
    }));
  }, [formData.monthlyTotals.totalNoOfLoans, docChargePerLoan]);

  const fetchSummaries = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getAllSummaries();
      console.log('Summaries:', data);
      setSummaries(data);
      setLoading(false);
    } catch (err) {
      showAlert(err.message, 'error');
      setLoading(false);
    }
  };

  const saveSummary = async () => {
    setLoading(true);
    try {
      const payload = {
        summaryMonth: formData.summaryMonth,
        dailyEntries: formData.dailyEntries,
        monthlyTotals: {
          openingBalance: formData.monthlyTotals.openingBalance,
          totalFundReleasedToF2F: formData.monthlyTotals.totalFundReleasedToF2F,
          totalNoOfLoans: formData.monthlyTotals.totalNoOfLoans,
          totalLoanAmount: formData.monthlyTotals.totalLoanAmount,
          totalDocCharge: formData.monthlyTotals.totalDocCharge,
          totalPrincipalInDue: formData.monthlyTotals.totalPrincipalInDue,
          totalInterestInDue: formData.monthlyTotals.totalInterestInDue,
          totalOfficeRent: formData.monthlyTotals.totalOfficeRent,
          totalStaffSalary: formData.monthlyTotals.totalStaffSalary,
          totalProfitWithdrawn: formData.monthlyTotals.totalProfitWithdrawn,
        },
      };

      let response;
      if (isEditMode) {
        response = await apiClient.updateSummary(editSummaryMonth, payload);
        showAlert('Summary updated successfully', 'success');
      } else {
        response = await apiClient.createSummary(payload);
        showAlert('Summary created successfully', 'success');
      }
      setFormData({
        ...formData,
        monthlyTotals: {
          ...formData.monthlyTotals,
          ...response.monthlyTotals,
        },
      });
      fetchSummaries();
      setLoading(false);
    } catch (err) {
      showAlert(err.message, 'error');
      setLoading(false);
    }
  };

  const handleDelete = async (summaryMonth) => {
    setLoading(true);
    try {
      await apiClient.deleteSummary(summaryMonth);
      await fetchSummaries();
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      summaryMonth: row.summaryMonth,
      dailyEntries: row.dailyEntries || [],
      monthlyTotals: {
        openingBalance: row.monthlyTotals?.openingBalance || 0,
        totalFundReleasedToF2F: row.monthlyTotals?.totalFundReleasedToF2F || 0,
        totalNoOfLoans: row.monthlyTotals?.totalNoOfLoans || 0,
        totalLoanAmount: row.monthlyTotals?.totalLoanAmount || 0,
        totalDocCharge: row.monthlyTotals?.totalDocCharge || 0,
        totalDueAmountPaid: row.monthlyTotals?.totalDueAmountPaid || 0,
        totalPrincipalInDue: row.monthlyTotals?.totalPrincipalInDue || 0,
        totalInterestInDue: row.monthlyTotals?.totalInterestInDue || 0,
        totalOfficeExpenses: row.monthlyTotals?.totalOfficeExpenses || 0,
        totalOfficeRent: row.monthlyTotals?.totalOfficeRent || 0,
        totalStaffSalary: row.monthlyTotals?.totalStaffSalary || 0,
        totalFinalIncome: row.monthlyTotals?.totalFinalIncome || 0,
        totalManagersShare: row.monthlyTotals?.totalManagersShare || 0,
        totalProfit: row.monthlyTotals?.totalProfit || 0,
        totalProfitWithdrawn: row.monthlyTotals?.totalProfitWithdrawn || 0,
        totalFundInMarket: row.monthlyTotals?.totalFundInMarket || 0,
        totalClosingBalance: row.monthlyTotals?.totalClosingBalance || 0,
      },
    });
    const estimatedDocCharge = row.monthlyTotals?.totalNoOfLoans > 0
      ? row.monthlyTotals.totalDocCharge / row.monthlyTotals.totalNoOfLoans
      : 200;
    setDocChargePerLoan(estimatedDocCharge || 200);
    setEditSummaryMonth(row.summaryMonth);
    setIsEditMode(true);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      summaryMonth: '',
      dailyEntries: [],
      monthlyTotals: {
        openingBalance: 0,
        totalFundReleasedToF2F: 0,
        totalNoOfLoans: 0,
        totalLoanAmount: 0,
        totalDocCharge: 0,
        totalDueAmountPaid: 0,
        totalPrincipalInDue: 0,
        totalInterestInDue: 0,
        totalOfficeExpenses: 0,
        totalOfficeRent: 0,
        totalStaffSalary: 0,
        totalFinalIncome: 0,
        totalManagersShare: 0,
        totalProfit: 0,
        totalProfitWithdrawn: 0,
        totalFundInMarket: 0,
        totalClosingBalance: 0,
      },
    });
    setDocChargePerLoan(200);
    setIsEditMode(false);
    setEditSummaryMonth('');
  };

  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'summaryMonth') {
      setFormData({ ...formData, [name]: value });
    } else if (name === 'docChargePerLoan') {
      setDocChargePerLoan(Number(value) || 200);
    } else {
      setFormData({
        ...formData,
        monthlyTotals: {
          ...formData.monthlyTotals,
          [name]: Number(value) || 0,
        },
      });
    }
  };

  const totals = summaries.reduce((acc, summary) => {
    const mt = summary.monthlyTotals || {};
    return {
      totalFundReleasedToF2F: acc.totalFundReleasedToF2F + (mt.totalFundReleasedToF2F || 0),
      totalNoOfLoans: acc.totalNoOfLoans + (mt.totalNoOfLoans || 0),
      totalLoanAmount: acc.totalLoanAmount + (mt.totalLoanAmount || 0),
      totalDueAmountPaid: acc.totalDueAmountPaid + (mt.totalDueAmountPaid || 0),
      totalPrincipalInDue: acc.totalPrincipalInDue + (mt.totalPrincipalInDue || 0),
      totalInterestInDue: acc.totalInterestInDue + (mt.totalInterestInDue || 0),
      totalOfficeExpenses: acc.totalOfficeExpenses + (mt.totalOfficeExpenses || 0),
      totalOfficeRent: acc.totalOfficeRent + (mt.totalOfficeRent || 0),
      totalStaffSalary: acc.totalStaffSalary + (mt.totalStaffSalary || 0),
      totalFinalIncome: acc.totalFinalIncome + (mt.totalFinalIncome || 0),
      totalManagersShare: acc.totalManagersShare + (mt.totalManagersShare || 0),
      totalProfit: acc.totalProfit + (mt.totalProfit || 0),
      totalProfitWithdrawn: acc.totalProfitWithdrawn + (mt.totalProfitWithdrawn || 0),
      totalFundInMarket: acc.totalFundInMarket + (mt.totalFundInMarket || 0),
      totalClosingBalance: mt.totalClosingBalance || 0,
      totalDocCharge: acc.totalDocCharge + (mt.totalDocCharge || 0),
    };
  }, {
    totalFundReleasedToF2F: 0,
    totalNoOfLoans: 0,
    totalLoanAmount: 0,
    totalDueAmountPaid: 0,
    totalPrincipalInDue: 0,
    totalInterestInDue: 0,
    totalOfficeExpenses: 0,
    totalOfficeRent: 0,
    totalStaffSalary: 0,
    totalFinalIncome: 0,
    totalManagersShare: 0,
    totalProfit: 0,
    totalProfitWithdrawn: 0,
    totalFundInMarket: 0,
    totalClosingBalance: 0,
    totalDocCharge: 0,
  });

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              <AccountIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#77c8c7' }} />
              Account Summary Management
            </Typography>
            <Divider sx={{ mb: 3, bgcolor: 'primary.light', height: 3, width: '100px', mx: 'auto' }} />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel id="navigate-label">Navigate To</InputLabel>
              <Select
                labelId="navigate-label"
                value={selectedPage}
                label="Navigate To"
                onChange={handleNavigate}
                sx={{

                  fontSize: '0.85rem',
                  fontWeight: 500,
                  backgroundColor: '#fff',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#77c8c7' },
                }}
              >
                <MenuItem value="/admin">Gold Loan</MenuItem>
                <MenuItem value="/velavan-finance">Velavan Finance</MenuItem>
                <MenuItem value="/due-matters">Due Matters</MenuItem>
                <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                {/* <MenuItem value="/graph">Graph</MenuItem> */}
                <MenuItem value="/monthly-graph">Monthly Graph</MenuItem>
              </Select>
            </FormControl>
            <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetForm(); setDialogOpen(true); }}
              sx={{ background: 'linear-gradient(45deg, #77c8c7 30%, #925f96 90%)', '&:hover': { background: 'linear-gradient(45deg, #925f96 30%, #77c8c7 90%)' } }}
            >
              Add New Summary
            </Button>
            </RoleGuard>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : summaries.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 8 }}>
              <Typography variant="h6">No account summaries found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click the "Add New Summary" button to create a new summary
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper sx={{ height: 500, bgcolor: 'background.paper', borderRadius: 8 }}>
               <Box sx={{ height: 500, width: "100%", mt: 2, overflowX: "auto" }}>
                
                <DataGrid
                  rows={summaries.map(summary => ({
                    id: summary.summaryMonth,
                    summaryMonth: summary.summaryMonth,
                    ...summary.monthlyTotals,
                  }))}
                  columns={columns}
                  pageSizeOptions={[12]}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-columnHeaders': { bgcolor: '#f1f5f9', color: 'black', fontWeight: '600' },
                    '& .MuiDataGrid-cell': { borderBottom: '1px solid #e5e7eb' },
                    '& .MuiDataGrid-root': { border: 'none' },
                    '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(119, 200, 199, 0.1)' },
                  }}
                />
                </Box>
              </Paper>

              <Box sx={{ mt: 5 }}>
                <Paper className="custom-paper">
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Funds
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Fund Released to VF:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalFundReleasedToF2F || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Fund in Market:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalFundInMarket || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Document Charges:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalDocCharge || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Paper className="custom-paper">
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Loans
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total No. of Loans:</Typography>
                      <Typography variant="body1">{totals.totalNoOfLoans || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Loan Amount:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalLoanAmount || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Paper className="custom-paper">
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Dues
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Due Amount Paid:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalDueAmountPaid || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Principal in Due:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalPrincipalInDue || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Interest in Due:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalInterestInDue || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Paper className="custom-paper">
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Expenses
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Office Expenses:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalOfficeExpenses || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Office Rent:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalOfficeRent || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Staff Salary:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalStaffSalary || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Paper className="custom-paper">
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Income & Profit
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Final Income:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalFinalIncome || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Manager's Share:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalManagersShare || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Profit:</Typography>
                      <Typography
                        variant="body1"
                        color={(totals.totalProfit || 0) < 0 ? 'error.main' : 'text.primary'}
                      >
                        Rs. {(totals.totalProfit || 0).toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold">Total Profit Withdrawn:</Typography>
                      <Typography variant="body1">Rs. {(totals.totalProfitWithdrawn || 0).toLocaleString('en-IN')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </>
          )}

          <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetForm(); }} maxWidth="md" fullWidth>
            <DialogTitle>{isEditMode ? 'Edit Account Summary' : 'Add New Account Summary'}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="summaryMonth"
                    label="Month/Year (e.g., April 2025)"
                    fullWidth
                    required
                    value={formData.summaryMonth}
                    onChange={handleFormChange}
                    disabled={isEditMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="openingBalance"
                    label="Opening Balance"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.openingBalance}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalFundReleasedToF2F"
                    label="Fund Released to VF"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalFundReleasedToF2F}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalNoOfLoans"
                    label="No. of Loans"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalNoOfLoans}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="docChargePerLoan"
                    label="Doc Charge per Loan"
                    type="number"
                    fullWidth
                    value={docChargePerLoan}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Doc Charge (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalDocCharge}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalLoanAmount"
                    label="Loan Amount"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalLoanAmount}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalPrincipalInDue"
                    label="Principal in Due"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalPrincipalInDue}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalInterestInDue"
                    label="Interest in Due"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalInterestInDue}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalOfficeRent"
                    label="Office Rent"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalOfficeRent}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalStaffSalary"
                    label="Staff Salary"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalStaffSalary}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="totalProfitWithdrawn"
                    label="Profit Withdrawn"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalProfitWithdrawn}
                    onChange={handleFormChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Due Amount Paid (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalDueAmountPaid}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fund in Market (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalFundInMarket}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Office Expenses (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalOfficeExpenses}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Final Income (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalFinalIncome}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Manager's Share (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalManagersShare}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Profit (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalProfit}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Closing Balance (Computed)"
                    type="number"
                    fullWidth
                    value={formData.monthlyTotals.totalClosingBalance}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setDialogOpen(false); resetForm(); }} disabled={loading}>Cancel</Button>
              <Button
                variant="contained"
                onClick={saveSummary}
                disabled={loading || !formData.summaryMonth}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {isEditMode ? 'Update' : 'Add'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => { setDialogOpen(false); resetForm(); }}
                disabled={loading}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={alert.open}
            autoHideDuration={6000}
            onClose={() => setAlert({ ...alert, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity} sx={{ width: '100%' }}>
              {alert.message}
            </Alert>
          </Snackbar>
        </Container>
      </ThemeProvider>
    </>
  );
}

export default AccountSummaryPage;  